/**
 * FilterConfigDialog Component
 *
 * Modal dialog for configuring filter details (operator and value)
 */

import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  FilterConfigDialogProps,
  FilterOperator,
  FilterOperatorLabels,
  FilterValue,
  FilterValueType,
} from '../types';

/**
 * Operator selector component - shown as clickable buttons
 */
interface OperatorSelectProps {
  operators: FilterOperator[];
  value: FilterOperator;
  onChange: (operator: FilterOperator) => void;
}

const OperatorSelect: React.FC<OperatorSelectProps> = ({
  operators,
  value,
  onChange,
}) => {
  return (
    <div className="flex flex-wrap gap-2">
      {operators.map((op) => (
        <button
          key={op}
          type="button"
          onClick={() => onChange(op)}
          className={`
            px-3 py-1.5 text-sm rounded-md
            border transition-colors
            ${value === op
              ? 'bg-accent-indigo text-white border-accent-indigo'
              : 'bg-bg-2 text-text-secondary border-light hover:bg-bg-3/60'
            }
          `}
        >
          {FilterOperatorLabels[op]}
        </button>
      ))}
    </div>
  );
};

/**
 * Value input component based on value type
 */
interface ValueInputProps {
  valueType: FilterValueType;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  options?: Array<{ value: FilterValue; label: string }>;
  placeholder?: string;
}

const ValueInput: React.FC<ValueInputProps> = ({
  valueType,
  value,
  onChange,
  options,
  placeholder,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus the input
    inputRef.current?.focus();
  }, []);

  // If we have options, render a select
  if (options && options.length > 0) {
    return (
      <select
        value={String(value)}
        onChange={(e) => {
          const option = options.find(
            (o) => String(o.value) === e.target.value
          );
          if (option) {
            onChange(option.value);
          }
        }}
        className="
          w-full px-3 py-2
          border border-light
          rounded-md
          bg-bg-2
          text-text-primary
          focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40
          text-sm
        "
      >
        {options.map((opt) => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }

  // Render appropriate input based on value type
  switch (valueType) {
    case FilterValueType.Number:
    case FilterValueType.NodeId:
      return (
        <input
          ref={inputRef}
          type="number"
          value={value as number}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          placeholder={placeholder || 'Enter number...'}
          className="
            w-full px-3 py-2
            border border-light
            rounded-md
            bg-bg-2
            text-text-primary
            focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40
            text-sm
          "
        />
      );

    case FilterValueType.Boolean:
      return (
        <select
          value={String(value)}
          onChange={(e) => onChange(e.target.value === 'true')}
          className="
            w-full px-3 py-2
            border border-light
            rounded-md
            bg-bg-2
            text-text-primary
            focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40
            text-sm
          "
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );

    case FilterValueType.String:
    default:
      return (
        <input
          ref={inputRef}
          type="text"
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'Enter value...'}
          className="
            w-full px-3 py-2
            border border-light
            rounded-md
            bg-bg-2
            text-text-primary
            focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold/40
            text-sm
          "
        />
      );
  }
};

/**
 * FilterConfigDialog Component
 *
 * @param filter - The filter being configured (null if closed)
 * @param definition - Filter definition
 * @param isNew - Whether creating a new filter or editing existing
 * @param onApply - Callback to apply changes
 * @param onCancel - Callback to cancel
 */
export const FilterConfigDialog: React.FC<FilterConfigDialogProps> = ({
  filter,
  definition,
  isNew,
  onApply,
  onCancel,
  nodes,
}) => {
  const [operator, setOperator] = useState<FilterOperator>(
    filter?.operator || definition?.defaultOperator || FilterOperator.Is
  );
  const [value, setValue] = useState<FilterValue>(filter?.value ?? '');

  const dialogRef = useRef<HTMLDivElement>(null);

  // Update local state when filter changes
  useEffect(() => {
    if (filter) {
      setOperator(filter.operator);
      setValue(filter.value);
    } else if (definition) {
      setOperator(definition.defaultOperator);
      setValue(definition.options?.[0]?.value ?? '');
    }
  }, [filter, definition]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  if (!filter || !definition) {
    return null;
  }

  const handleApply = () => {
    onApply({
      ...filter,
      operator,
      value,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleApply();
    }
  };

  // Check if definition has a custom config component
  if (definition.ConfigComponent) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div
          ref={dialogRef}
          className="
            bg-bg-1
            border border-light
            rounded-2xl shadow-2xl
            max-w-md w-full mx-4
            max-h-[90vh] overflow-auto
          "
        >
          <definition.ConfigComponent
            filter={filter}
            definition={definition}
            onUpdate={(op, val) => {
              onApply({ ...filter, operator: op, value: val });
            }}
            onClose={onCancel}
            nodes={nodes}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div
        ref={dialogRef}
        className="
          bg-bg-1
          border border-light
          rounded-2xl shadow-2xl
          max-w-sm w-full mx-4
          overflow-hidden
        "
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-light">
          <h3 className="text-lg font-display font-medium text-text-primary">
            {isNew ? 'Add Filter' : 'Edit Filter'}: {definition.label}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="
              p-1 rounded-full
              text-text-tertiary hover:text-text-secondary
              hover:bg-bg-3/60
              transition-colors
            "
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {/* Operator */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">
              Condition
            </label>
            <OperatorSelect
              operators={definition.supportedOperators}
              value={operator}
              onChange={setOperator}
            />
          </div>

          {/* Value */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">
              Value
            </label>
            <ValueInput
              valueType={definition.valueType}
              value={value}
              onChange={setValue}
              options={definition.options}
              placeholder={definition.placeholder}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-4 py-3 border-t border-light bg-bg-2/40">
          <button
            type="button"
            onClick={onCancel}
            className="
              px-4 py-2 text-sm font-medium
              text-text-secondary
              bg-bg-2
              border border-light
              rounded-md
              hover:bg-bg-3/60
              focus:outline-none focus:ring-2 focus:ring-accent-gold/40
              transition-colors
            "
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="
              px-4 py-2 text-sm font-medium
              text-white
              bg-accent-indigo hover:bg-accent-indigo/90
              rounded-md
              focus:outline-none focus:ring-2 focus:ring-accent-gold/40 focus:ring-offset-2 focus:ring-offset-bg-1
              transition-colors
            "
          >
            {isNew ? 'Add Filter' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterConfigDialog;
